// Supabase client error interceptor — reports { data, error } failures to Sentry.
//
// Supabase returns errors as values inside HTTP 200 responses. Sentry's fetch
// instrumentation never sees these. This interceptor patches .from(), .rpc(),
// .storage, and .auth to call onError when error is non-null.
//
// The { data, error } contract is NOT altered — callers see the same result.

/** Any callable, with no assumptions about its parameters or result. */
type UnknownFn = (...args: unknown[]) => unknown;

/** A plain object we index dynamically. */
type UnknownRecord = Record<PropertyKey, unknown>;

/** The `.then` implementation of a thenable Supabase query builder. */
type ThenFn = (onfulfilled?: unknown, onrejected?: unknown) => unknown;

export interface SupabaseErrorContext {
  /** Table name for .from() queries. */
  table?: string;
  /** Method identifier (e.g. "rpc:my_func", "storage:bucket:upload", "auth:getSession"). */
  method?: string;
}

export type SupabaseErrorReporter = (
  error: { message: string; code?: string; details?: string; hint?: string },
  context: SupabaseErrorContext,
) => void;

/** The error payload shape carried on a Supabase `{ data, error }` result. */
type SupabaseErrorPayload = Parameters<SupabaseErrorReporter>[0];

/**
 * The subset of the Supabase client surface this module patches. Every member
 * is optional — callers pass real clients, but the runtime checks are what
 * decide whether a hook is installed.
 */
interface InstrumentableClient {
  from?: (table: string) => unknown;
  rpc?: (...args: unknown[]) => unknown;
  storage?: { from?: (bucket: string) => unknown };
  auth?: unknown;
}

/**
 * Pull the `error` off a `{ data, error }` result.
 *
 * Supabase / PostgREST errors are PLAIN OBJECTS ({ message, details, hint,
 * code }) — never `Error` instances. Do not route them through
 * `instanceof Error`; it renders "[object Object]".
 *
 * Returns null when the result carries no error, matching the original
 * `result?.error` truthiness check exactly.
 */
function extractError(result: unknown): SupabaseErrorPayload | null {
  if (!result || typeof result !== 'object') return null;
  const error = (result as { error?: unknown }).error;
  if (!error) return null;
  return error as SupabaseErrorPayload;
}

/** Mirrors the original `x && typeof x === 'object' && typeof x.then === 'function'`. */
function isThenable(value: unknown): value is UnknownRecord {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

/**
 * Instrument a SupabaseClient so every operation that returns { error } is
 * reported via onError BEFORE the result reaches the caller.
 *
 * Covers: .from(), .rpc(), .storage.from().*(), .auth.*()
 * Does NOT alter the return value or the API surface.
 */
export function instrumentSupabaseClient<T extends object>(
  client: T,
  onError: SupabaseErrorReporter,
): T {
  // The client's real types are far too generic to patch through, so the
  // members we touch are read off a narrow structural view of it.
  const target = client as InstrumentableClient;

  // .from(table) — query builders (select/insert/update/delete/upsert)
  const originalFrom = target.from;
  if (typeof originalFrom === 'function') {
    const boundFrom = originalFrom.bind(client);
    target.from = (table: string) => wrapBuilder(boundFrom(table), onError, { table });
  }

  // .rpc(fn, params) — remote procedure calls
  const originalRpc = target.rpc;
  if (typeof originalRpc === 'function') {
    const boundRpc = originalRpc.bind(client);
    target.rpc = (...args: unknown[]) =>
      wrapBuilder(boundRpc(...args), onError, { method: `rpc:${String(args[0])}` });
  }

  // .storage.from(bucket).* — storage operations
  const storage = target.storage;
  const originalStorageFrom = storage?.from;
  if (storage && typeof originalStorageFrom === 'function') {
    const boundStorageFrom = originalStorageFrom.bind(storage);
    storage.from = (bucket: string) => {
      const bucketApi = boundStorageFrom(bucket);
      return wrapResultMethods(bucketApi, onError, { method: `storage:${bucket}` }, [
        'upload', 'download', 'remove', 'list', 'move', 'copy',
        'createSignedUrl', 'createSignedUrls', 'getPublicUrl',
      ]);
    };
  }

  // .auth.* — auth operations that return { data, error }
  if (target.auth) {
    wrapResultMethods(target.auth, onError, { method: 'auth' }, [
      'signInWithPassword', 'signInWithOAuth', 'signInWithOtp',
      'signUp', 'signOut', 'refreshSession', 'getSession', 'getUser',
      'resetPasswordForEmail', 'updateUser',
    ]);
  }

  return client;
}

/**
 * Proxy that intercepts .then() on thenable query builders to check for errors.
 * Recursively wraps every chainable method return value so the interception
 * survives full chains like .from('t').select('*').eq('id', 1).single().
 */
function wrapBuilder(
  builder: unknown,
  onError: SupabaseErrorReporter,
  ctx: SupabaseErrorContext,
): unknown {
  if (!builder || typeof builder !== 'object') return builder;

  return new Proxy(builder as UnknownRecord, {
    get(target, prop, receiver) {
      // Intercept .then() — this is where query results arrive
      if (prop === 'then') {
        const originalThen = target.then;
        if (typeof originalThen !== 'function') return originalThen;
        return (onfulfilled?: UnknownFn, onrejected?: unknown) =>
          (originalThen as ThenFn).call(
            target,
            (result: unknown) => {
              const error = extractError(result);
              if (error) {
                try { onError(error, ctx); } catch { /* never break the caller */ }
              }
              return onfulfilled ? onfulfilled(result) : result;
            },
            onrejected,
          );
      }

      const val = Reflect.get(target, prop, receiver);
      if (typeof val === 'function') {
        return (...args: unknown[]) => {
          const result = (val as UnknownFn).apply(target, args);
          // If the return value is thenable (another builder), wrap it too
          if (isThenable(result)) {
            return wrapBuilder(result, onError, ctx);
          }
          return result;
        };
      }
      return val;
    },
  });
}

/**
 * Wrap specific async methods on an object (storage bucket, auth) that return
 * { data, error } directly (not via thenable builder chains).
 */
function wrapResultMethods(
  obj: unknown,
  onError: SupabaseErrorReporter,
  ctx: SupabaseErrorContext,
  methods: string[],
): unknown {
  const target = obj as UnknownRecord;
  for (const method of methods) {
    const original = target[method];
    if (typeof original !== 'function') continue;
    const bound = (original as UnknownFn).bind(target);
    target[method] = async (...args: unknown[]) => {
      const result = await bound(...args);
      const error = extractError(result);
      if (error) {
        try {
          onError(error, { ...ctx, method: `${ctx.method}:${method}` });
        } catch { /* never break the caller */ }
      }
      return result;
    };
  }
  return obj;
}
